<div class="business-overview-page">
  @if (isLoading) {
    <app-loader></app-loader>
  }

  <section class="overview-hero">
    <div class="hero-copy">
      <span class="hero-kicker">Salon Owner Dashboard</span>

      <h1>
        Welcome <span>{{ userName }}</span>
      </h1>

      <p>
        Review revenue, bookings, customer activity, and salon operations from
        one focused business command center.
      </p>
    </div>

    <div class="hero-actions">
      <button
        type="button"
        class="overview-btn overview-btn--light"
        [routerLink]="['/admin/business/appointments']"
        [queryParams]="{ view: 'calendar' }"
      >
        Calendar
      </button>

      <button
        type="button"
        class="overview-btn overview-btn--dark"
        (click)="downloadReport()"
      >
        Download Report
      </button>
    </div>
  </section>

  <section class="kpi-grid">
    <article class="kpi-card kpi-card--primary">
      <div>
        <span>Revenue</span>
        <strong>{{ totalRevenue | currency }}</strong>
        <p>Total revenue currently captured in the business overview.</p>
      </div>

      <img [src]="assetUrl + 'icons/dollar-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Appointments</span>
        <strong>{{ cardsData?.appointments_booked || 0 }}</strong>
        <p>
          {{ completionRate }}% completed · {{ cancellationRate }}% cancelled
        </p>
      </div>

      <img [src]="assetUrl + 'icons/calendar-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Customers</span>
        <strong>{{ cardsData?.total_customers || 0 }}</strong>
        <p>{{ averageCustomerValue | currency }} average customer value</p>
      </div>

      <img [src]="assetUrl + 'icons/users-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Staff</span>
        <strong>{{ cardsData?.total_staff || 0 }}</strong>
        <p>{{ revenuePerStaff | currency }} revenue per staff member</p>
      </div>

      <img [src]="assetUrl + 'icons/users-border.svg'" alt="" />
    </article>
  </section>

  <section class="metric-grid">
    <article class="metric-card">
      <span>Revenue / Appointment</span>
      <strong>{{ revenuePerAppointment | currency }}</strong>
      <p>Average value generated per booking.</p>
    </article>

    <article class="metric-card">
      <span>Upcoming Revenue</span>
      <strong>{{ upcomingAppointmentRevenue | currency }}</strong>
      <p>Projected value from upcoming visible appointments.</p>
    </article>

    <article class="metric-card metric-card--success">
      <span>Completed</span>
      <strong>{{ completedAppointments }}</strong>
      <p>Appointments successfully completed.</p>
    </article>

    <article class="metric-card metric-card--warning">
      <span>Pending</span>
      <strong>{{ pendingAppointments }}</strong>
      <p>Appointments still waiting to be completed.</p>
    </article>

    <article class="metric-card metric-card--danger">
      <span>Cancelled</span>
      <strong>{{ cancelledAppointments }}</strong>
      <p>Bookings cancelled in the appointment set.</p>
    </article>
  </section>

  <section class="main-grid">
    <article class="panel revenue-panel">
      <header class="panel-head">
        <div>
          <span class="panel-kicker">Revenue Performance</span>
          <h2>Revenue Trend</h2>
          <p>
            Compare business performance across daily, weekly, monthly,
            quarterly, and yearly views.
          </p>
        </div>

        <button
          type="button"
          class="overview-btn overview-btn--light"
          (click)="downloadReport()"
        >
          View Report
        </button>
      </header>

      <div class="chart-toolbar">
        <glo-option-switch
          [options]="periodOptions"
          [selected]="selectedPeriod"
          (changed)="onPeriodChange($event)"
        ></glo-option-switch>
      </div>

      <div class="chart-shell">
        <canvas #chartCanvas></canvas>
      </div>
    </article>

    <aside class="panel business-health-panel">
      <header class="panel-head panel-head--stacked">
        <div>
          <span class="panel-kicker">Business Health</span>
          <h2>Operating Snapshot</h2>
          <p>Quick operational indicators for the owner.</p>
        </div>
      </header>

      <div class="health-list">
        <div class="health-row">
          <div>
            <span>Completion Rate</span>
            <strong>{{ completionRate }}%</strong>
          </div>
          <div class="health-bar">
            <span [style.width.%]="completionRate"></span>
          </div>
        </div>

        <div class="health-row">
          <div>
            <span>Cancellation Rate</span>
            <strong>{{ cancellationRate }}%</strong>
          </div>
          <div class="health-bar health-bar--danger">
            <span [style.width.%]="cancellationRate"></span>
          </div>
        </div>

        <div class="health-row">
          <div>
            <span>Revenue / Staff</span>
            <strong>{{ revenuePerStaff | currency }}</strong>
          </div>
        </div>

        <div class="health-row">
          <div>
            <span>Customer Value</span>
            <strong>{{ averageCustomerValue | currency }}</strong>
          </div>
        </div>
      </div>
    </aside>
  </section>

  <section class="bottom-grid">
    <article class="panel calendar-panel">
      <header class="panel-head">
        <div>
          <span class="panel-kicker">Schedule</span>
          <h2>Booking Calendar</h2>
          <p>{{ visibleRangeText }}</p>
        </div>

        <button
          type="button"
          class="overview-btn overview-btn--light"
          [routerLink]="['/admin/business/appointments']"
          [queryParams]="{ view: 'calendar' }"
        >
          View All
        </button>
      </header>

      <div class="calendar-shell">
        <full-calendar [options]="calendarOptions"></full-calendar>
      </div>
    </article>

    <article class="panel appointments-panel">
      <header class="panel-head">
        <div>
          <span class="panel-kicker">Upcoming Work</span>
          <h2>Next Appointments</h2>
          <p>Upcoming bookings with service, salon, duration, and value.</p>
        </div>

        <button
          type="button"
          class="overview-btn overview-btn--light"
          [routerLink]="['/admin/business/appointments']"
          [queryParams]="{ view: 'table' }"
        >
          View All
        </button>
      </header>

      <div class="appointment-list">
        @if (filteredAppointments().length === 0) {
          <glo-empty node="appointments"></glo-empty>
        } @else {
          <article
            *ngFor="let appt of filteredAppointments()"
            class="appointment-card"
          >
            <div class="date-card">
              <span>{{ formatDateString(appt.start_time, "MMM") }}</span>
              <strong>{{ formatDateString(appt.start_time, "DD") }}</strong>
            </div>

            <div class="appointment-info">
              <div class="appointment-meta">
                <span>
                  {{ formatDateString(appt.start_time, "MMM D, YYYY") }} at
                  {{ formatTime(appt.start_time) }}
                </span>

                <span class="status-pill" [ngClass]="appt.status">
                  {{ appt.status }}
                </span>
              </div>

              <h3>
                {{ appt.items[0]?.["service"]?.["name"] }}
                {{
                  appt.items.length > 1 ? " + " + (appt.items.length - 1) : ""
                }}
              </h3>

              <p>
                {{ appt.salon.name }} • {{ appt.total_duration_minutes }} mins
              </p>
            </div>

            <strong class="appointment-price">
              {{ appt.total_price | currency }}
            </strong>
          </article>
        }
      </div>
    </article>
  </section>
</div>
